import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { PUBLIC_SITE_ORIGIN } from '@shared/chrome/copy';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { formatNumber } from '@shared/format';
import { resolveErrorCopy } from '@shared/errors/catalog';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { ListSearch } from '@shared/ui/ListSearch';
import { ExternalLinkIcon } from '@shared/ui/icons';
import { PlusIcon } from '@shared/ui/icons/nav';
import { SurfaceError } from '@shared/ui/SurfaceError';
import { useToast } from '@shared/ui/toast/ToastProvider';
import { ApiError } from '@shared/api/client';

import { Pager } from '@shared/ui/pager';

import { distinctRoles, PeopleSection, RoleBadges, SectionAction, ShownLine } from '../_shared/components';

/**
 * How many rows one page of the register holds.
 *
 * A reading-side number rather than a server-owned one, and that is the whole
 * difference between this pager and the console's others: the route answers a
 * bare array with no page metadata at all, so there is nothing to divide the
 * service's own page size into. Chosen to match what the paged surfaces show.
 */
const PAGE_SIZE = 10;
import { MAX_PAGE_SIZE, PEOPLE_ROUTES } from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import { peopleErrors } from '../_shared/errors';
import { useCreateCreditProfile } from '../_shared/mutations';
import { creditProfilesScope, useCreditedItems, useCreditProfiles } from '../_shared/queries';
import type { CreditProfileRecord } from '../_shared/types';

import { copy } from './copy';
import { PersonDialog } from './People.PersonDialog';
import type { PersonValues } from './forms.schema';

const TABLE_COLUMNS = 4;

/**
 * `/people` — the credited-person register.
 *
 * **Nobody here holds an account, and there is no column that says so.** These
 * records are profiles for credited people who are *not* platform users, so an
 * account is not a field this register leaves empty — it is a thing that does
 * not occur here. Accounts, with their roles, blocks and entitlements, live on
 * their own surface.
 *
 * **No row menu, and that is the destructive-action rule applied rather than
 * ignored.** That rule exists so a destructive action never sits inline in a
 * row; both of this table's row-level entries are navigations, and a menu
 * holding two links would hide them behind a click for no gain. There is no
 * delete either: the catalog names create, edit and maintain — no destructive
 * verb — so drawing one would mean inventing the action and then inventing its
 * confirmation.
 *
 * **The register searches on the service and keeps its place in the URL.**
 * `GET /admin/creditProfiles` has always taken `term` and `page_size`; this
 * surface sent neither, so it showed the default 20 rows of 34 and fourteen
 * people could not be reached at all — no search, no filter, no sort, no page
 * size, and no other route that lists them. The top-bar field submits to the
 * content list, which is a different subject.
 *
 * **The page and the term live in the query string**, so an address can be
 * copied, Back returns to the previous page of this register instead of leaving
 * the section entirely, and a reload lands where the operator was.
 */
export function PeoplePage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [params, setParams] = useSearchParams();

  const term = params.get('q') ?? '';
  const query = useCreditProfiles(term);
  const createProfile = useCreateCreditProfile();
  const [dialogOpen, setDialogOpen] = useState(false);

  const rows = query.data ?? [];
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // A page beyond the last is clamped rather than rendered empty: `?page=9` over
  // two pages is a stale address, and an empty table is a worse answer than the
  // last page of the register.
  const page = Math.min(Math.max(1, Number(params.get('page')) || 1), pageCount);
  const visible = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /**
   * Write the traversal state to the address bar.
   *
   * `replace` for the term and `push` for the page: typing is one continuous
   * act and should not leave a history entry per keystroke, while turning a page
   * is a step a reader expects Back to undo.
   */
  function updateParams(next: { q?: string; page?: number }, replace: boolean) {
    const merged = new URLSearchParams(params);
    if (next.q !== undefined) {
      if (next.q) merged.set('q', next.q);
      else merged.delete('q');
      // A new term restarts the traversal; keeping page 3 would open on rows the
      // new term may not have.
      merged.delete('page');
    }
    if (next.page !== undefined) {
      if (next.page > 1) merged.set('page', String(next.page));
      else merged.delete('page');
    }
    setParams(merged, { replace });
  }

  useEffect(() => {
    document.title = copy.documentTitle;
  }, []);

  function commit(values: PersonValues) {
    createProfile.mutate(
      {
        display_name: values.displayName,
        // An empty textarea is an absent biography, not the empty string: the
        // service serialises an absent optional out of the payload entirely,
        // and a stored `""` would draw an empty paragraph on the public page.
        biography: values.biography.length > 0 ? values.biography : null,
      },
      {
        onSuccess: (created) => {
          setDialogOpen(false);
          toast.show(copy.added(created.display_name), 'success');
        },
        onError: (error) => {
          setDialogOpen(false);
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.code : undefined;
          toast.show(resolveErrorCopy(code, status, peopleErrors).message, 'danger');
        },
      },
    );
  }

  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <PeopleSection
        id="people-heading"
        heading={copy.section.heading}
        context={copy.section.context}
        action={
          <SectionAction onClick={() => setDialogOpen(true)} icon={<PlusIcon />}>
            {copy.section.add}
          </SectionAction>
        }
      >
        <ListSearch
          label={copy.search.label}
          placeholder={copy.search.placeholder}
          value={term}
          onChange={(next) => updateParams({ q: next }, true)}
        />

        {query.isError ? (
          <SurfaceError
            error={query.error}
            catalog={peopleErrors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: creditProfilesScope() })}
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <PeopleTable>
              <RowSkeleton columns={TABLE_COLUMNS} />
            </PeopleTable>
          </>
        ) : null}

        {query.data ? (
          rows.length === 0 ? (
            <EmptyState message={term ? copy.search.empty(term) : copy.states.empty} />
          ) : (
            <>
              <PeopleTable>
                {visible.map((person) => (
                  <PersonRow key={person.id} person={person} />
                ))}
              </PeopleTable>

              {/*
                **The pager walks rows this page already holds, and that is the
                only honest option here.** `GET /admin/creditProfiles` answers a
                bare array — no `page_size`, no `total`, no `has_next` — so there
                is no cursor to send and no second page to ask for. Chunking the
                register keeps a long table readable; it does not reach past
                whatever ceiling the service applied to the array, and the line
                beneath still says so.
              */}
              {/*
                A hundred rows is the route's ceiling, not the register's size:
                a table that fills to exactly the bound is the one case where
                «all of them» and «as many as fit» look identical, so the
                surface says which it is.
              */}
              {rows.length >= MAX_PAGE_SIZE ? (
                <p className="mb-0 mt-4 max-w-measure text-xs text-ink-muted">
                  {copy.count.ceiling(formatNumber(MAX_PAGE_SIZE))}
                </p>
              ) : null}

              <Pager
                label={copy.pager.label}
                page={page}
                pageCount={pageCount}
                shown={visible.length}
                total={rows.length}
                noun={{
                  one: copy.pager.shownOne,
                  two: copy.pager.shownTwo,
                  few: copy.pager.shownFew,
                  many: copy.pager.shownMany,
                }}
                hasPrevious={page > 1}
                hasNext={page * PAGE_SIZE < rows.length}
                onPrevious={() => updateParams({ page: page - 1 }, false)}
                onNext={() => updateParams({ page: page + 1 }, false)}
                onGoToPage={(target) => updateParams({ page: target }, false)}
              />

              <ShownLine shown={rows.length} note={copy.count.note} more={copy.count.more} />
            </>
          )
        ) : null}
      </PeopleSection>

      <PersonDialog
        open={dialogOpen}
        busy={createProfile.isPending}
        onCancel={() => setDialogOpen(false)}
        onCommit={commit}
      />
    </>
  );
}

/**
 * One row.
 *
 * **The roles and the works count are derived, and they are derived per row on
 * purpose.** A credit is a relation carrying its own role, so neither figure
 * lives on the profile record the register reads — and no route aggregates them
 * across the register. What exists is the credit-holder body per person, so the
 * row reads its own, which is the narrowest composition the published routes
 * can express. The alternative — dropping two of the four columns the pack
 * draws — would have been a silent deletion of the surface.
 *
 * The read is the same query the person's own page uses, keyed on the same
 * slug, so opening a row after seeing it costs nothing.
 *
 * **A pending derived cell is blank rather than zero.** A `٠` that later
 * becomes `١١` is a wrong figure shown confidently; the cell holds its shape
 * until the count is real.
 */
function PersonRow({ person }: { person: CreditProfileRecord }) {
  const credits = useCreditedItems(person.id);
  const items = credits.data?.items ?? [];

  return (
    <tr>
      {/*
        Name over slug, and **no portrait beside them**: nothing in this console
        supplies one, and a thumbnail column would be an affordance no row
        grants. The person's own page draws the neutral face because that page
        is the record's head; a register row is a line of text.

        The link takes ink rather than the accent, because a whole column of red
        names competes with the one button on the page allowed to mean «press
        here».
      */}
      <DTableRowHeader>
        <span className="grid">
          <Link
            to={PEOPLE_ROUTES.person(person.slug)}
            className="font-semibold text-ink no-underline hover:underline"
          >
            {person.display_name}
          </Link>
          {/* The slug is this record's only identifier: Latin, mono, isolated. */}
          <span className="mono mt-px text-xs text-ink-muted">
            <bdi>{person.slug}</bdi>
          </span>
        </span>
      </DTableRowHeader>

      <td>
        {credits.isSuccess ? (
          <RoleBadges roles={distinctRoles(items)} empty={sharedCopy.noRoles} />
        ) : (
          <span className="text-xs text-ink-muted" aria-hidden="true">
            —
          </span>
        )}
      </td>

      <td className="whitespace-nowrap [font-variant-numeric:tabular-nums]">
        {credits.isSuccess ? <bdi>{formatNumber(items.length)}</bdi> : <span aria-hidden="true">—</span>}
      </td>

      <td className="w-[10rem]">
        {/*
          The one link in this table that leaves the host. The public page is
          what the catalog asks the operator to maintain, so it is reachable
          from the row that maintains it.
        */}
        <a
          href={`${PUBLIC_SITE_ORIGIN}/people/${encodeURIComponent(person.slug)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hit-target inline-flex items-center gap-2 rounded-sm px-3 text-xs text-ink-muted no-underline hover:bg-hover-bg hover:text-ink"
        >
          {sharedCopy.publicPage}
          <ExternalLinkIcon />
        </a>
      </td>
    </tr>
  );
}

/**
 * The table frame, shared by the skeleton and the data states so neither
 * reflows the other. Four columns and a `32rem` floor: the first cell is a
 * two-line identity beside a face and the last carries a fully-labelled link,
 * and neither may crush.
 */
function PeopleTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.caption} minInlineSize="32rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.person}</th>
            <th scope="col">{copy.columns.roles}</th>
            <th scope="col">{copy.columns.works}</th>
            <th scope="col">
              <span className="sr-only">{copy.columns.publicPage}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}
